import React, { lazy } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, SparklesIcon, ShieldCheckIcon } from 'lucide-react';
// The "calm" payoff beat — a single, clean Giga app window shown as a realistic
// screenshot. Where TwelveTabProblem piles up cluttered tabs, this resolves to
// ONE focused window: Giga's activity feed with a finished task, real chat
// bubble, completed sub-steps across connected tools, and a "Handled" status.
// Built from real DOM/Tailwind so it reads as a genuine product screenshot.
const STEPS: {
  slug: string;
  label: string;
}[] = [{
  slug: 'zoom',
  label: 'Pulled action items from your standup'
}, {
  slug: 'linear',
  label: 'Logged 4 follow-up tasks in Linear'
}, {
  slug: 'gmail',
  label: 'Sent the recap to the team'
}];
function StepIcon({
  slug
}: {
  slug: string;
}) {
  return <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-[#F4F0EA] border border-black/[0.04] shrink-0" data-id="element-3354">
      <img src={`https://cdn.simpleicons.org/${slug}`} alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-3355" />
    </span>;
}
export function WorkYouCantGetTo() {
  const reduced = !!useReducedMotion();
  return <section className="relative w-full overflow-hidden bg-[#FAF8F4] py-24 md:py-32" data-id="element-3356">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-3357">
        {/* Visual (Left) — single resolved Giga window screenshot */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative h-[440px] md:h-[500px] w-full flex items-center justify-center order-2 md:order-1" data-id="element-3358">
          {/* soft warm halo */}
          <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{
          background: 'radial-gradient(55% 50% at 50% 45%, rgba(234,88,12,0.08) 0%, transparent 70%)'
        }} data-id="element-3359" />

          {/* concentric ambient rings — Giga quietly watching */}
          {!reduced && [0, 1, 2].map(i => <motion.span key={i} aria-hidden="true" className="absolute rounded-full border border-[#EA580C]/15" style={{
          width: 320 + i * 70,
          height: 320 + i * 70
        }} animate={{
          scale: [1, 1.04, 1],
          opacity: [0.5, 0.2, 0.5]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: i * 0.6
        }} data-id="element-3360" />)}

          <motion.div className="relative z-10 w-[330px] md:w-[390px] rounded-2xl bg-white border border-black/[0.06] shadow-[0_40px_90px_-30px_rgba(43,33,26,0.5)] overflow-hidden" animate={reduced ? undefined : {
          y: [0, -10, 0]
        }} transition={reduced ? undefined : {
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-3361">
            {/* macOS chrome + app header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-black/[0.05] bg-[#FBFAF8]" data-id="element-3362">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]" data-id="element-3363" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" data-id="element-3364" />
              <span className="w-3 h-3 rounded-full bg-[#28C840]" data-id="element-3365" />
              <div className="ml-3 flex items-center gap-1.5" data-id="element-3366">
                <span className="flex items-center justify-center w-4 h-4 rounded-md bg-[#EA580C]" data-id="element-3367">
                  <SparklesIcon size={9} className="text-white" data-id="element-3368" />
                </span>
                <span className="text-[11px] font-bold text-neutral-700" data-id="element-3369">
                  Giga
                </span>
              </div>
              <span className="ml-auto flex items-center gap-1 text-[9px] font-semibold text-[#10B981]" data-id="element-3370">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" data-id="element-3371" />
                Active
              </span>
            </div>

            <div className="p-4 space-y-3" data-id="element-3372">
              {/* Giga message bubble */}
              <div className="flex items-start gap-2.5" data-id="element-3373">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#EA580C] shrink-0" data-id="element-3374">
                  <SparklesIcon size={13} className="text-white" data-id="element-3375" />
                </span>
                <div className="flex-1 rounded-2xl rounded-tl-md bg-[#F4F0EA] px-3 py-2.5" data-id="element-3376">
                  <p className="text-[11px] leading-relaxed text-neutral-700" data-id="element-3377">
                    Noticed your standup wrapped — I went ahead and{' '}
                    <span className="font-semibold text-neutral-900" data-id="element-3378">
                      cleaned up the follow-ups
                    </span>{' '}
                    so nothing slips.
                  </p>
                </div>
              </div>

              {/* Completed-task card */}
              <div className="rounded-xl border border-black/[0.06] bg-white shadow-sm overflow-hidden" data-id="element-3379">
                <div className="flex items-center justify-between px-3 py-2 border-b border-black/[0.04] bg-[#FBFAF8]" data-id="element-3380">
                  <span className="text-[10px] font-bold text-neutral-700" data-id="element-3381">
                    Standup follow-ups
                  </span>
                  <span className="flex items-center gap-1 text-[9px] font-bold text-[#10B981] bg-[#10B981]/10 rounded-full px-2 py-0.5" data-id="element-3382">
                    <CheckIcon size={9} strokeWidth={3} data-id="element-3383" />
                    Handled
                  </span>
                </div>
                <div className="divide-y divide-neutral-100" data-id="element-3384">
                  {STEPS.map((step, i) => <motion.div key={step.slug} className="flex items-center gap-2.5 px-3 py-2" initial={reduced ? false : {
                  opacity: 0,
                  x: -8
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.2 + i * 0.18,
                  ease: [0.16, 1, 0.3, 1]
                }} data-id="element-3385">
                      <StepIcon slug={step.slug} data-id="element-3386" />
                      <span className="flex-1 text-[10px] font-medium text-neutral-600 leading-tight" data-id="element-3387">
                        {step.label}
                      </span>
                      <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#10B981] shrink-0" data-id="element-3388">
                        <CheckIcon size={9} strokeWidth={3.5} className="text-white" data-id="element-3389" />
                      </span>
                    </motion.div>)}
                </div>
              </div>

              {/* Footer status row */}
              <div className="flex items-center justify-between pt-0.5" data-id="element-3390">
                <span className="flex items-center gap-1.5 text-[9px] font-medium text-neutral-400" data-id="element-3391">
                  <ShieldCheckIcon size={11} className="text-[#EA580C]" data-id="element-3392" />
                  You stayed in control
                </span>
                <span className="text-[9px] font-semibold text-neutral-400" data-id="element-3393">
                  Done in 12s
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Text (Right) */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative z-10 max-w-xl mx-auto md:mx-0 text-center md:text-left order-1 md:order-2" data-id="element-3394">
          <h2 className="text-3xl md:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#2B211A]" data-id="element-3395">
            You didn't ask.
            <br data-id="element-3396" />
            Giga noticed.
          </h2>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-neutral-500 font-medium leading-relaxed" data-id="element-3397">
            Other AI tools stop at answers. Giga goes further.
          </p>
        </motion.div>
      </div>
    </section>;
}